import type { ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Modal from 'LuxKit/Modal/Modal'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'
import useCruiseCabinDescriptions from 'hooks/Cruise/useCruiseCabinDescriptions'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'
import type { CruiseDepartureCabinPrice } from 'lib/cruises/cruiseDeparturePricing'

interface Props {
  offer: App.CruiseOffer
  price: CruiseDepartureCabinPrice
  renderSoldOutContent?: (category: string) => ReactNode
}

/**
 * Surface-neutral content for one cruise cabin category. Consumers own grid
 * layout and may opt into their own sold-out follow-up action.
 */
function CruiseDepartureCabinPriceItem({ offer, price, renderSoldOutContent }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const cabinDescriptions = useCruiseCabinDescriptions()
  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])
  const burnResult = useOfferPageBurnPoints(
    price.primaryPrice.total,
    loyaltyProductType ?? 'cruise_group_3',
    undefined,
    { disabled: !loyaltyProductType || !price.hasPrice },
  )
  const showBurnPrice = burnResult.enabled && burnResult.priceInPoints !== undefined
  const openCabinInfoModal = useCallback(() => {
    showModal(<Modal title={cabinTypeLabels[price.category] ?? price.category}>
      <BodyText variant="medium">
        {cabinDescriptions[price.category]}
      </BodyText>
    </Modal>)
  }, [cabinDescriptions, cabinTypeLabels, price.category, showModal])

  return <>
    {price.hasPrice && <PriceRowPriceCaption onInfoIconClick={openCabinInfoModal}>
      {intl.formatMessage(
        {
          id: 'b1qBznBcSCuPOJgb3M3m',
          defaultMessage: '{category} from',
          description: 'Cabin category price label with from prefix in cabin selection',
        },
        { category: cabinTypeLabels[price.category] ?? price.category },
      )}
    </PriceRowPriceCaption>}

    {price.hasPrice && showBurnPrice && <OfferPageBurnPriceDisplay
      totalPrice={price.primaryPrice.total}
      pointsToApply={burnResult.pointsToBurn}
      priceInPoints={burnResult.priceInPoints}
      memberPrice={price.memberPrice}
      size="S"
      saleUnit={price.primaryPrice.type === 'night' ? 'night' : 'person'}
      stacked
    />}

    {!!price.primaryPrice.total && !showBurnPrice && <LuxPlusPriceStack
      size="S"
      price={price.primaryPrice.total}
      memberPrice={price.memberPrice}
      saleUnit={price.primaryPrice.type}
    />}

    {price.secondaryPrice && !showBurnPrice && <Group direction="horizontal" gap={4}>
      <Caption variant="large" colour="light-foreground-alt" as="span">
        <FormatCurrency value={price.secondaryPrice.total} />
      </Caption>
      <Caption variant="large" colour="light-foreground-alt" as="span">
        {intl.formatMessage({
          id: 'oScuwLDRHT-zAp6w64wC',
          defaultMessage: '/{priceType}',
          description: 'Per-unit price type label for cruise secondary price (e.g. /person)',
        }, { priceType: price.secondaryPrice.type })}
      </Caption>
    </Group>}

    {!price.hasPrice && <Group direction="vertical" gap={8}>
      <Caption variant="large" weight="normal" colour="light-foreground-alt">
        {cabinTypeLabels[price.category] ?? price.category}
      </Caption>
      <Group direction="vertical" gap={4}>
        <Heading variant="heading5" colour="light-foreground-alt">
          <FormattedMessage
            id="yMfXxKPttoB6lFemIeuH"
            defaultMessage="Sold out"
            description="Label indicating cabin category is sold out in cabin selection"
          />
        </Heading>
        {renderSoldOutContent?.(price.category)}
      </Group>
    </Group>}

    {!!price.discountPercentage && <PriceRowValueDiscount
      type="estimate"
      discountPercentage={price.discountPercentage}
    />}

    {price.hasPrice && <Caption variant="medium" colour="light-foreground-alt">
      <FormattedMessage
        id="5Tekcjnyh3bWzSGZIiez"
        defaultMessage="Twin Share"
        description="Label indicating price is for twin share accommodation"
      />
    </Caption>}
  </>
}

export default CruiseDepartureCabinPriceItem
